/**
 * ThemeContext - V17.37 Dark Mode Support
 *
 * Provides theme state (light/dark) across the application.
 * Users can toggle between light and dark mode from the sidebar.
 * Theme preference is persisted in localStorage.
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

export type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  isDark: boolean;
}

/** `<meta name="theme-color">` per theme (index.html ships the light one). */
export const THEME_COLOR: Record<Theme, string> = { light: '#2563eb', dark: '#111827' };

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Initialize from localStorage only - default to light mode
  // V17.37.1: Light mode is the default. Users can toggle to dark mode if they prefer.
  const [theme, setThemeState] = useState<Theme>(() => {
    // Only use saved preference, otherwise default to light
    const saved = localStorage.getItem('vtw_theme');
    if (saved === 'light' || saved === 'dark') {
      return saved;
    }
    // Default to light mode (not system preference)
    return 'light';
  });

  // Apply theme class to document root
  useEffect(() => {
    const root = window.document.documentElement;

    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    // Brief 2026-10-27-001 (SC-7, audit F23): the phone status bar follows the
    // theme — dark is the mobile header's gray-900, light stays the brand blue.
    // Runs at load (first render, with a saved "dark") and on every toggle.
    document
      .querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')
      .forEach((meta) => meta.setAttribute('content', THEME_COLOR[theme]));

    // Persist to localStorage
    localStorage.setItem('vtw_theme', theme);
  }, [theme]);

  // V17.37.1: Removed system theme change listener
  // Theme is now user-controlled only - defaults to light, user can toggle to dark

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState(prev => prev === 'light' ? 'dark' : 'light');
  }, []);

  return (
    <ThemeContext.Provider value={{
      theme,
      setTheme,
      toggleTheme,
      isDark: theme === 'dark'
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
